import { motion } from "motion/react";
import { shallow } from "zustand/shallow";

import type { Cell } from "@edclub/crosswords-engine";
import { cn } from "@edclub/utils";

import { CellInput } from "./CellInput";

const cellVariants = {
  initial: (custom: {
    revealed: boolean;
    status?: "correct" | "incorrect";
    active: boolean;
    playable: boolean;
  }) => ({
    y: -20,
    opacity: 0,
    backgroundColor: custom.playable
      ? custom.revealed
        ? "#ccc"
        : "#fff"
      : "rgba(0,0,0,0)",
    scale: 1,
  }),
  animate: (custom: {
    revealed: boolean;
    status?: "correct" | "incorrect";
    active: boolean;
    playable: boolean;
  }) => {
    const base = {
      y: 0,
      opacity: 1,
      backgroundColor:
        custom.active && !custom.revealed
          ? "#fff3da"
          : custom.active || custom.revealed
            ? "#ccc"
            : custom.playable
              ? "#fff"
              : "rgba(0,0,0,0)",
      scale: 1,
    };

    if (custom.status) {
      return {
        ...base,
        scale: 1,
        backgroundColor: custom.status === "correct" ? "#a0e9a0" : "#e9a0a0",
      };
    }

    return base;
  },
};

interface CrosswordCellProps {
  rowIndex: number;
  colIndex: number;
  cell: Cell;
  activeCell: Cell | null;
  activeWordId: number | null;
  cellSize: number;
  handleSetActiveCell: (playable: boolean, cell: any) => () => void;
}

export function CrosswordCell({
  rowIndex,
  colIndex,
  cell,
  activeWordId,
  cellSize,
  activeCell,
  handleSetActiveCell,
}: CrosswordCellProps) {
  const cellKey = cell.coord || `cell-${rowIndex}-${colIndex}`;
  const playable = Boolean(cell.letter);
  const cellInActiveWord = activeWordId
    ? cell.words.includes(activeWordId)
    : false;
  const isActive = shallow(cell, activeCell);

  const ariaLabel =
    `Cell at row ${rowIndex + 1}, column ${colIndex + 1}` +
    (cell.revealed ? ", revealed" : "");

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (playable && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      handleSetActiveCell(playable, cell)();
    }
  };

  return (
    <motion.div
      key={cellKey}
      role="gridcell"
      tabIndex={playable ? 0 : -1}
      aria-label={ariaLabel}
      aria-selected={isActive ? true : undefined}
      onKeyDown={handleKeyDown}
      custom={{
        revealed: cell.revealed,
        active: cellInActiveWord,
        playable,
        status: cell.status,
      }}
      variants={cellVariants}
      initial="initial"
      animate="animate"
      transition={{
        type: "spring",
        stiffness: 200,
        damping: 10,
        delay: rowIndex * 0.025 + colIndex * 0.025,
      }}
      style={{
        width: `${cellSize}px`,
        height: `${cellSize}px`,
        lineHeight: `${cellSize}px`,
        fontSize: `${cellSize / 2}px`,
      }}
      className={cn(
        "relative flex items-center justify-center rounded text-center font-semibold text-[#42576e]",
        cell.letter !== "" ? "border border-gray-300" : "cursor-default",
      )}
      onClick={handleSetActiveCell(playable, cell)}
    >
      {isActive && !cell.status ? (
        <CellInput
          cellSize={cellSize - 4 /* margin */}
          row={rowIndex}
          col={colIndex}
          cell={cell}
        />
      ) : (
        <div
          className="select-none"
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {cell.revealed && cell.letter
            ? cell.letter.toUpperCase()
            : cell.userAnswer || ""}
        </div>
      )}

      {cell.clue && (
        <span className="absolute top-0 left-0.5 inline-flex text-xs font-semibold text-[#329DA7] select-none">
          {cell.clue}
        </span>
      )}
    </motion.div>
  );
}
